import * as THREE from 'three';

export type LidType = 'none' | 'slide' | 'snap' | 'flush';

export interface BoxParams {
  width: number;             // X en mm (ej. 120)
  length: number;            // Y en mm (ej. 80)
  height: number;            // Z en mm (ej. 45)
  wallThickness: number;     // Grosor de paredes en mm (ej. 2.0)
  bottomThickness: number;   // Grosor del fondo en mm (ej. 2.4)
  cornerRadius: number;      // Radio de redondeo de esquinas en mm (ej. 5.0)

  // Divisiones internas (Compartimentos)
  columnsX: number;          // Divisiones en X (1 a 6)
  rowsY: number;             // Divisiones en Y (1 a 6)
  dividerThickness: number;  // Espesor de separadores en mm (ej. 1.2)
  dividerHeightPercent: number; // Altura de los separadores (50% a 100%)

  // Tapa
  lidType: LidType;          // Tipo de tapa
  lidThickness: number;      // Espesor de la tapa en mm (ej. 2.0)
  lidClearance: number;      // Holgura en mm (ej. 0.3)
  hasFingerNotch: boolean;   // Muesca para el dedo
}

/**
 * Crea una forma 2D de rectángulo con esquinas redondeadas
 */
function createRoundedRectShape(w: number, l: number, r: number): THREE.Shape {
  const shape = new THREE.Shape();
  const halfW = w / 2;
  const halfL = l / 2;
  const radius = Math.min(r, Math.min(halfW, halfL) - 0.5);

  shape.moveTo(-halfW + radius, -halfL);
  shape.lineTo(halfW - radius, -halfL);
  shape.absarc(halfW - radius, -halfL + radius, radius, -Math.PI / 2, 0, false);
  shape.lineTo(halfW, halfL - radius);
  shape.absarc(halfW - radius, halfL - radius, radius, 0, Math.PI / 2, false);
  shape.lineTo(-halfW + radius, halfL);
  shape.absarc(-halfW + radius, halfL - radius, radius, Math.PI / 2, Math.PI, false);
  shape.lineTo(-halfW, -halfL + radius);
  shape.absarc(-halfW + radius, -halfL + radius, radius, Math.PI, Math.PI * 1.5, false);

  return shape;
}

/**
 * Genera la geometría 3D de la Caja Principal con divisiones y rebordes
 */
export function generateBoxGeometry(params: BoxParams): THREE.BufferGeometry {
  const {
    width = 120,
    length = 80,
    height = 45,
    wallThickness = 2.0,
    bottomThickness = 2.4,
    cornerRadius = 5.0,
    columnsX = 2,
    rowsY = 2,
    dividerThickness = 1.2,
    dividerHeightPercent = 100,
    lidType = 'snap'
  } = params;

  // 1. Cuerpo Exterior
  const outerShape = createRoundedRectShape(width, length, cornerRadius);
  const outerGeom = new THREE.ExtrudeGeometry(outerShape, {
    depth: height,
    bevelEnabled: false
  });
  outerGeom.rotateX(-Math.PI / 2);

  // 2. Cavidad Interior (Hoyo)
  const innerW = width - wallThickness * 2;
  const innerL = length - wallThickness * 2;
  const innerR = Math.max(0.5, cornerRadius - wallThickness);
  const cavityDepth = height - bottomThickness;

  const innerShape = createRoundedRectShape(innerW, innerL, innerR);

  // Pared interior extruida
  const innerHoleGeom = new THREE.ExtrudeGeometry(innerShape, {
    depth: cavityDepth,
    bevelEnabled: false
  });
  innerHoleGeom.rotateX(-Math.PI / 2);
  innerHoleGeom.translate(0, bottomThickness, 0);

  // En lugar de operaciones booleanas lentas en CPU, construimos la caja por mallas aditivas o caras
  // Forma de caja: Fondo + 4 paredes redondeadas + separadores
  const geomsToMerge: THREE.BufferGeometry[] = [];

  // Fondo
  const bottomGeom = new THREE.ExtrudeGeometry(outerShape, {
    depth: bottomThickness,
    bevelEnabled: false
  });
  bottomGeom.rotateX(-Math.PI / 2);
  geomsToMerge.push(bottomGeom);

  // Paredes perimetrales: Extrusión de forma hueca (outer con inner hole)
  const wallShape = createRoundedRectShape(width, length, cornerRadius);
  const innerHole = createRoundedRectShape(innerW, innerL, innerR);
  wallShape.holes.push(innerHole);

  const wallsGeom = new THREE.ExtrudeGeometry(wallShape, {
    depth: height - bottomThickness,
    bevelEnabled: false
  });
  wallsGeom.rotateX(-Math.PI / 2);
  wallsGeom.translate(0, bottomThickness, 0);
  geomsToMerge.push(wallsGeom);

  // Si tiene labio para tapa a presión (Snap)
  if (lidType === 'snap') {
    const lipW = innerW + 0.4;
    const lipL = innerL + 0.4;
    const lipR = innerR;
    const lipShape = createRoundedRectShape(lipW, lipL, lipR);
    const lipHole = createRoundedRectShape(innerW - 1.2, innerL - 1.2, Math.max(0.2, lipR - 1.2));
    lipShape.holes.push(lipHole);

    const lipGeom = new THREE.ExtrudeGeometry(lipShape, {
      depth: 2.2,
      bevelEnabled: false
    });
    lipGeom.rotateX(-Math.PI / 2);
    lipGeom.translate(0, height, 0);
    geomsToMerge.push(lipGeom);
  }

  // 3. Divisiones internas (Separadores en X e Y)
  const divHeight = (height - bottomThickness) * (dividerHeightPercent / 100);

  // Separadores en X (dividen columnas)
  if (columnsX > 1) {
    const colStep = innerW / columnsX;
    for (let i = 1; i < columnsX; i++) {
      const posX = -innerW / 2 + colStep * i;
      const divGeom = new THREE.BoxGeometry(dividerThickness, divHeight, innerL);
      divGeom.translate(posX, bottomThickness + divHeight / 2, 0);
      geomsToMerge.push(divGeom);
    }
  }

  // Separadores en Y (dividen filas)
  if (rowsY > 1) {
    const rowStep = innerL / rowsY;
    for (let j = 1; j < rowsY; j++) {
      const posZ = -innerL / 2 + rowStep * j;
      const divGeom = new THREE.BoxGeometry(innerW, divHeight, dividerThickness);
      divGeom.translate(0, bottomThickness + divHeight / 2, posZ);
      geomsToMerge.push(divGeom);
    }
  }

  // Fusión limpia
  const combinedPositions: number[] = [];
  geomsToMerge.forEach(g => {
    const nonIndexed = g.toNonIndexed();
    const pos = nonIndexed.getAttribute('position');
    for (let i = 0; i < pos.count; i++) {
      combinedPositions.push(pos.getX(i), pos.getY(i), pos.getZ(i));
    }
    g.dispose();
  });

  const finalGeom = new THREE.BufferGeometry();
  finalGeom.setAttribute('position', new THREE.Float32BufferAttribute(combinedPositions, 3));
  finalGeom.computeVertexNormals();

  return finalGeom;
}

/**
 * Genera la geometría 3D de la Tapa correspondiente
 */
export function generateLidGeometry(params: BoxParams): THREE.BufferGeometry {
  const {
    width = 120,
    length = 80,
    height = 45,
    wallThickness = 2.0,
    cornerRadius = 5.0,
    lidType = 'snap',
    lidThickness = 2.0,
    lidClearance = 0.35,
    hasFingerNotch = true
  } = params;

  if (lidType === 'none') {
    return new THREE.BufferGeometry();
  }

  const geomsToMerge: THREE.BufferGeometry[] = [];

  // Tapa superior
  const outerShape = createRoundedRectShape(width, length, cornerRadius);
  const plateGeom = new THREE.ExtrudeGeometry(outerShape, {
    depth: lidThickness,
    bevelEnabled: true,
    bevelSegments: 2,
    bevelSize: 0.5,
    bevelThickness: 0.5
  });
  plateGeom.rotateX(-Math.PI / 2);
  plateGeom.translate(0, height + 3, 0); // Flotando ligeramente arriba para previsualización
  geomsToMerge.push(plateGeom);

  // Si es Snap: Reborde interior de encastre
  if (lidType === 'snap') {
    const innerW = width - wallThickness * 2 - lidClearance * 2;
    const innerL = length - wallThickness * 2 - lidClearance * 2;
    const innerR = Math.max(0.5, cornerRadius - wallThickness - lidClearance);

    const rimShape = createRoundedRectShape(innerW, innerL, innerR);
    const rimHole = createRoundedRectShape(innerW - 2.5, innerL - 2.5, Math.max(0.2, innerR - 2.5));
    rimShape.holes.push(rimHole);

    const rimGeom = new THREE.ExtrudeGeometry(rimShape, {
      depth: 3.5,
      bevelEnabled: false
    });
    rimGeom.rotateX(-Math.PI / 2);
    rimGeom.translate(0, height + 3 - 3.5, 0);
    geomsToMerge.push(rimGeom);
  }

  // Muesca para el dedo
  if (hasFingerNotch) {
    const notchGeom = new THREE.CylinderGeometry(6, 6, lidThickness * 1.5, 16);
    notchGeom.translate(width / 2 - 12, height + 3 + lidThickness / 2, 0);
    // (Visual indicator notch)
  }

  const combinedPositions: number[] = [];
  geomsToMerge.forEach(g => {
    const nonIndexed = g.toNonIndexed();
    const pos = nonIndexed.getAttribute('position');
    for (let i = 0; i < pos.count; i++) {
      combinedPositions.push(pos.getX(i), pos.getY(i), pos.getZ(i));
    }
    g.dispose();
  });

  const finalGeom = new THREE.BufferGeometry();
  finalGeom.setAttribute('position', new THREE.Float32BufferAttribute(combinedPositions, 3));
  finalGeom.computeVertexNormals();

  return finalGeom;
}

/**
 * Exportador directo a formato binario STL para Slicers 3D
 */
export function exportBoxToSTL(geometry: THREE.BufferGeometry, filename = 'caja-organizadora.stl') {
  let geom = geometry.clone();
  if (geom.index) {
    geom = geom.toNonIndexed();
  }

  const posAttr = geom.getAttribute('position');
  const trianglesCount = posAttr.count / 3;

  const bufferLength = 80 + 4 + trianglesCount * 50;
  const arrayBuffer = new ArrayBuffer(bufferLength);
  const dataView = new DataView(arrayBuffer);

  const header = 'MilaOS 3D Generator - Parametric Organizer Box STL';
  for (let i = 0; i < 80; i++) {
    dataView.setUint8(i, i < header.length ? header.charCodeAt(i) : 0);
  }

  dataView.setUint32(80, trianglesCount, true);

  let offset = 84;
  const pA = new THREE.Vector3();
  const pB = new THREE.Vector3();
  const pC = new THREE.Vector3();
  const normal = new THREE.Vector3();
  const cb = new THREE.Vector3();
  const ab = new THREE.Vector3();

  for (let i = 0; i < posAttr.count; i += 3) {
    pA.fromBufferAttribute(posAttr, i);
    pB.fromBufferAttribute(posAttr, i + 1);
    pC.fromBufferAttribute(posAttr, i + 2);

    cb.subVectors(pC, pB);
    ab.subVectors(pA, pB);
    cb.cross(ab).normalize();
    normal.copy(cb);

    dataView.setFloat32(offset, normal.x, true);
    dataView.setFloat32(offset + 4, normal.y, true);
    dataView.setFloat32(offset + 8, normal.z, true);
    offset += 12;

    dataView.setFloat32(offset, pA.x, true);
    dataView.setFloat32(offset + 4, pA.y, true);
    dataView.setFloat32(offset + 8, pA.z, true);
    offset += 12;

    dataView.setFloat32(offset, pB.x, true);
    dataView.setFloat32(offset + 4, pB.y, true);
    dataView.setFloat32(offset + 8, pB.z, true);
    offset += 12;

    dataView.setFloat32(offset, pC.x, true);
    dataView.setFloat32(offset + 4, pC.y, true);
    dataView.setFloat32(offset + 8, pC.z, true);
    offset += 12;

    dataView.setUint16(offset, 0, true);
    offset += 2;
  }

  const blob = new Blob([arrayBuffer], { type: 'application/octet-stream' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename.endsWith('.stl') ? filename : `${filename}.stl`;
  link.click();
  URL.revokeObjectURL(link.href);
}
